import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import "../../styles/Dashboard.css";
import { deleteData, getData } from "../../services/Request";
import { toast } from "react-toastify";
import { useLoader } from "../../context/LoaderContext";
import { formatDate } from "../../utils/dateFormat";

const getSaleId = (sale) => sale?.id ?? sale?._id ?? "";

const normaliseList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.rows)) return payload.data.rows;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.sales)) return payload.sales;
  return [];
};

const parseSaleItems = (sale) => {
  if (!sale || typeof sale?.items !== "string") return sale;
  try {
    const parsedItems = JSON.parse(sale.items);
    return { ...sale, items: Array.isArray(parsedItems) ? parsedItems : [] };
  } catch (error) {
    console.error("Failed to parse sale.items", error);
    return { ...sale, items: [] };
  }
};

export default function InvoiceList() {
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();
  const [sales, setSales] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const hasFetchedInitial = useRef(false);
  const startIndex = useMemo(() => (page - 1) * pageSize, [page, pageSize]);

  const fetchSales = async ({ withLoader = false } = {}) => {
    try {
      if (withLoader) {
        showLoader();
      } else {
        setListLoading(true);
      }
      const response = await getData(
        `api/sales?page=${page}&pageSize=${pageSize}&invoice=true&search=${encodeURIComponent(
          search,
        )}`,
      );
      const normalisedSales = normaliseList(response).map(parseSaleItems);
      setSales(normalisedSales);
      const pagination =
        response?.pagination || response?.data?.pagination || {};
      setTotalPages(pagination?.totalPages || 1);
    } catch (error) {
      toast.error("Sales list not found.");
      console.error("Failed to fetch sales", error);
    } finally {
      if (withLoader) {
        hideLoader();
      } else {
        setListLoading(false);
      }
    }
  };

  const handleDeleteSale = async (saleId) => {
    if (!saleId) {
      toast.error("Quotation ID missing.");
      return;
    }
    const toastId = toast.loading("Deleting quotation...");
    try {
      showLoader();
      await deleteData(`api/sales/${saleId}`);
      toast.update(toastId, {
        render: "Quotation deleted.",
        type: "success",
        isLoading: false,
        autoClose: 1500,
      });
      setConfirmDeleteId(null);
      await fetchSales();
    } catch (error) {
      toast.update(toastId, {
        render: "Quotation could not be deleted. Please try again.",
        type: "error",
        isLoading: false,
        autoClose: 2000,
      });
      console.error("Failed to delete sale", error);
    } finally {
      hideLoader();
    }
  };

  const openDeleteModal = (saleId) => setConfirmDeleteId(saleId);
  const closeDeleteModal = () => setConfirmDeleteId(null);

  useEffect(() => {
    if (hasFetchedInitial.current) return;
    hasFetchedInitial.current = true;
    fetchSales({ withLoader: true });
  }, []);

  useEffect(() => {
    fetchSales();
  }, [page, search]);

  return (
    <>
      <div className="app-shell">
        <Sidebar />
        <div className="app-content">
          <Navbar />
          <div className="dashboard-page">
            <main className="dashboard">
              <section className="sample-page">
                <div
                  className="sample-page__header"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "12px",
                  }}
                >
                  <h1 style={{ marginTop: 3 }}>All Invoices</h1>
                </div>

                <div className="seller-card seller-table-card">
                  <div
                    className="list-actions"
                    style={{ marginBottom: "10px" }}
                  >
                    <input
                      type="search"
                      placeholder="Search Invoice"
                      value={search}
                      onChange={(e) => {
                        setPage(1);
                        setSearch(e.target.value);
                      }}
                    />
                  </div>
                  <div className="seller-table-wrapper">
                    <table className="seller-table">
                      <thead>
                        <tr>
                          <th>No</th>
                          <th>Invoice No</th>
                          <th>Buyer</th>
                          <th>Seller</th>
                          <th>Total qty</th>
                          <th>Total price</th>
                          <th>Date</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sales.length === 0 && (
                          <tr>
                            <td colSpan="10" className="empty-state">
                              {listLoading
                                ? "Loading sales..."
                                : "No sales found."}
                            </td>
                          </tr>
                        )}
                        {sales.map((sale, index) => {
                          const saleId = getSaleId(sale);
                          const rowKey = saleId ?? `sale-${index}`;
                          const items = Array.isArray(sale?.items)
                            ? sale.items
                            : [];
                          const isFbrInvoice =
                            sale?.fbr === true ||
                            sale?.fbr === 1 ||
                            sale?.fbr === "1";

                          return (
                            <tr key={rowKey}>
                              <td>{startIndex + index + 1}</td>
                              <td>{sale?.id}</td>
                              <td>
                                {sale?.buyerBusinessName ||
                                  sale?.buyerNTNCNIC ||
                                  "-"}
                              </td>
                              <td>
                                {sale?.sellerBusinessName ||
                                  sale?.sellerNTNCNIC ||
                                  "-"}
                              </td>
                              <td>{items[0]?.quantity || 0}</td>
                              <td>{items[0]?.totalValues || 0}</td>

                              <td>
                                {sale?.invoiceDate
                                  ? formatDate(sale.invoiceDate)
                                  : "-"}
                              </td>
                              <td>
                                <div className="table-actions">
                                  {!isFbrInvoice && (
                                    <>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          navigate(`/invoice-edit/${saleId}`, {
                                            state: { sale },
                                          })
                                        }
                                        disabled={!saleId}
                                      >
                                        Edit
                                      </button>

                                      <button
                                        type="button"
                                        className="danger"
                                        onClick={() => openDeleteModal(saleId)}
                                        disabled={!saleId}
                                      >
                                        Delete
                                      </button>
                                    </>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() =>
                                      navigate(`/invoice/${saleId}`)
                                    }
                                    disabled={!saleId}
                                  >
                                    View invoice
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="table-pagination">
                    <button
                      type="button"
                      disabled={page <= 1 || listLoading}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      Prev
                    </button>
                    <span>
                      Page {page} of {totalPages}
                    </span>
                    <button
                      type="button"
                      disabled={page >= totalPages || listLoading}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </section>
            </main>
          </div>
        </div>
      </div>
      {confirmDeleteId && (
        <div className="modal-backdrop">
          <div className="modal-card">
            <h3>Delete quotation</h3>
            <p>This action cannot be undone. Delete this quotation?</p>
            <div className="modal-actions">
              <button
                type="button"
                className="outline-btn"
                onClick={closeDeleteModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="primary-btn danger"
                onClick={() => handleDeleteSale(confirmDeleteId)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
